import {
  type InfiniteData,
  type QueryClient,
  queryOptions,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { useApi } from "@/core/api";
import { RUN_PAGE_SIZE, RUN_POLL_INTERVAL_MS } from "./constants";
import {
  DOCUMENT_POLL_INTERVAL_MS,
  DOCUMENT_STATUS,
} from "./document-constants";
import { readReportOutput } from "./read-report-output";
import type {
  ProfitabilityDocument,
  ProfitabilityRun,
  ReportDataPage,
  ReportDataQuery,
  ReportOutput,
  ReportRequest,
  RunHistoryPage,
} from "./types";
import {
  enrichProfitabilityPeriod,
  enrichProfitabilityRun,
  isActiveRun,
  reportPath,
} from "./utils";

export const reportKey = (businessId: string | null) =>
  ["profitability", businessId] as const;

/** Mutable report views; completed output and source-file bytes keep their caches. */
export const invalidateReportViews = (
  client: QueryClient,
  businessId: string | null,
) =>
  client.invalidateQueries({
    predicate: ({ queryKey }) =>
      queryKey[0] === "profitability" &&
      queryKey[1] === businessId &&
      ([
        "history",
        "overview",
        "trends",
        "record-history",
        "findings",
        "run",
        "run-review",
        "reports",
      ].includes(String(queryKey[2])) ||
        (queryKey[2] === "document" && queryKey.length === 4)),
  });

const enrichReportHistory = (
  history: InfiniteData<RunHistoryPage, number>,
) => ({
  ...history,
  pages: history.pages.map((page) => ({
    ...page,
    items: page.items.map(enrichProfitabilityPeriod),
  })),
});

export const useReportHistory = (
  businessId: string | null,
  reportId?: string,
  workflowVersion: 1 | 2 = 2,
) => {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: [
      ...reportKey(businessId),
      "history",
      workflowVersion,
      reportId ?? "all",
    ],
    initialPageParam: 1,
    select: enrichReportHistory,
    queryFn: ({ pageParam, signal }) =>
      api
        .get<RunHistoryPage>(
          `/businesses/${businessId}/profitability_report_periods`,
          {
            params: {
              page: pageParam,
              page_size: RUN_PAGE_SIZE,
              workflow_version: workflowVersion,
              ...(reportId ? { report_id: reportId } : {}),
            },
            signal,
          },
        )
        .then((res) => res.data),
    getNextPageParam: (lastPage) =>
      lastPage.has_more ? lastPage.next_page : null,
    enabled: !!businessId,
    // Every loaded page refetches together, so a run that finishes while the
    // list is open lands on whichever page it is already sitting on.
    refetchInterval: (query) =>
      query.state.data?.pages.some((page) =>
        page.items.some((period) => isActiveRun(period.latest_attempt)),
      )
        ? RUN_POLL_INTERVAL_MS
        : false,
    retry: false,
  });
};

export const useReportRun = (businessId: string | null, runId?: string) => {
  const api = useApi();
  return useQuery({
    queryKey: [...reportKey(businessId), "run", runId],
    select: enrichProfitabilityRun,
    queryFn: ({ signal }) =>
      api
        .get<{ run: ProfitabilityRun }>(reportPath(businessId, runId), {
          signal,
        })
        .then((res) => res.data.run),
    enabled: !!businessId && !!runId,
    refetchInterval: (query) =>
      query.state.data && isActiveRun(query.state.data)
        ? RUN_POLL_INTERVAL_MS
        : false,
    retry: false,
  });
};

export const useCreateReport = (
  businessId: string | null,
  workflowVersion: 1 | 2 = 2,
) => {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (request: ReportRequest) =>
      api
        .post<{
          run: ProfitabilityRun;
        }>(reportPath(businessId), {
          ...request,
          workflow_version: workflowVersion,
        })
        .then((res) => res.data.run),
    onSuccess: (run) => {
      client.setQueryData([...reportKey(businessId), "run", run.id], run);
      void invalidateReportViews(client, businessId);
    },
  });
};

export const documentsPath = (businessId: string | null) =>
  `/businesses/${businessId}/profitability_documents`;

export const useUploadReportDocuments = (businessId: string | null) => {
  const api = useApi();
  return useMutation({
    mutationFn: (files: File[]) => {
      const formData = new FormData();
      files.forEach((file) => formData.append("files[]", file, file.name));
      return api
        .post<{
          documents: ProfitabilityDocument[];
        }>(documentsPath(businessId), formData)
        .then((res) => res.data.documents);
    },
  });
};

export const useReportDocuments = (
  businessId: string | null,
  ids: string[],
) => {
  const api = useApi();
  return useQuery({
    queryKey: [...reportKey(businessId), "documents", ids],
    queryFn: ({ signal }) =>
      api
        .get<{
          documents: ProfitabilityDocument[];
        }>(documentsPath(businessId), { params: { ids }, signal })
        .then((res) => res.data.documents),
    enabled: !!businessId && ids.length > 0,
    placeholderData: (previous) =>
      previous?.filter((document) => ids.includes(document.id)),
    refetchInterval: (query) =>
      query.state.data?.some(
        (document) => document.status === DOCUMENT_STATUS.pending,
      )
        ? DOCUMENT_POLL_INTERVAL_MS
        : false,
    retry: false,
  });
};

export const useDeleteReportDocument = (businessId: string | null) => {
  const api = useApi();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.delete(`${documentsPath(businessId)}/${documentId}`),
  });
};

/**
 * A completed report usually downloads on the first request. When the stored
 * file is missing the API rebuilds it and hands back a polling token, so this
 * keeps asking until the file exists, the caller navigates away, or the wait
 * runs past its bound.
 */
export const downloadReport = async (
  api: AxiosInstance,
  businessId: string,
  runId: string,
  signal: AbortSignal,
): Promise<Blob> => {
  let token: string | undefined;
  // A bounded wait also handles an export worker that is no longer available.
  for (let attempt = 0; attempt < 150; attempt++) {
    signal.throwIfAborted();
    const response = await api.get<Blob>(
      `${reportPath(businessId, runId)}/workbook/download`,
      { responseType: "blob", params: token ? { token } : undefined, signal },
    );
    if (response.status === 200) return response.data;
    if (response.status !== 202)
      throw new Error("The download failed. Try again.");
    const result = JSON.parse(await response.data.text()) as {
      status: string;
      token?: string;
    };
    if (result.status !== "processing" || !result.token)
      throw new Error("The export response was invalid. Try again.");
    token = result.token;
    await new Promise<void>((resolve, reject) => {
      const abort = () => {
        clearTimeout(timer);
        reject(new DOMException("Download cancelled", "AbortError"));
      };
      const timer = setTimeout(() => {
        signal.removeEventListener("abort", abort);
        resolve();
      }, RUN_POLL_INTERVAL_MS);
      signal.addEventListener("abort", abort, { once: true });
      if (signal.aborted) abort();
    });
  }
  throw new Error("The export is taking too long. Try the download again.");
};

export const reportOutputKey = (businessId: string | null, runId: string) => [
  ...reportKey(businessId),
  "output",
  runId,
];

/** One run's output request, for `useQuery` or `useQueries`. */
export const reportOutputOptions = (
  api: AxiosInstance,
  businessId: string,
  runId: string,
) =>
  queryOptions({
    queryKey: reportOutputKey(businessId, runId),
    queryFn: ({ signal }) => readReportOutput(api, businessId, runId, signal),
    retry: false,
    // A run with no declared output has nothing to ask for.
    enabled: !!runId,
  });

export const useReportOutput = (
  businessId: string,
  runId: string,
  /** Shown while this run's output loads, such as another period's. */
  placeholderData?: () => ReportOutput | undefined,
) => {
  const api = useApi();
  return useQuery({
    ...reportOutputOptions(api, businessId, runId),
    placeholderData,
  });
};

/** The most rows one data request returns, and so the most a chart can ask for. */
export const REPORT_DATA_PAGE_SIZE = 500;

/**
 * Aggregates run over all selected rows before the API pages the result.
 *
 * Every page is read before this resolves, so a page that does not move the
 * offset forward is an error rather than another turn of the loop: it would
 * otherwise ask for the same rows forever.
 */
export const readReportData = async (
  api: AxiosInstance,
  businessId: string,
  runId: string,
  query: ReportDataQuery,
  signal: AbortSignal,
): Promise<ReportDataPage> => {
  const rows: ReportDataPage["rows"] = [];
  let offset = 0;
  for (;;) {
    signal.throwIfAborted();
    const { data } = await api.post<ReportDataPage>(
      `${reportPath(businessId, runId)}/data`,
      { ...query, offset, limit: REPORT_DATA_PAGE_SIZE },
      { signal },
    );
    rows.push(...data.rows);
    if (data.next_offset === null) return { ...data, rows };
    if (data.next_offset <= offset)
      throw new Error("The report data stopped paging. Try again.");
    offset = data.next_offset;
  }
};

export const useReportData = (
  businessId: string,
  runId: string,
  query: ReportDataQuery,
  { enabled = true }: { enabled?: boolean } = {},
) => {
  const api = useApi();
  return useQuery({
    queryKey: [...reportKey(businessId), "data", runId, query],
    queryFn: ({ signal }) =>
      readReportData(api, businessId, runId, query, signal),
    retry: false,
    enabled,
  });
};
